import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Shield, Copy, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TwoFactorSetup = ({ userId, onSetupComplete }) => {
    const [otpData, setOtpData] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        generateOTPSecret();
    }, []);

    const generateOTPSecret = async () => {
        setIsLoading(true);
        setError('');
        
        try {
            const response = await axios.post(`${API}/auth/otp/generate`, {
                user_id: userId
            });
            
            setOtpData(response.data);
        } catch (error) {
            setError('Dështoi në gjenerimin e 2FA setup. Provoni përsëri.');
            console.error('OTP generation error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const verifyOTPCode = async () => {
        if (!verificationCode || verificationCode.length !== 6) {
            setError('Ju lutem vendosni një kod valid 6-shifror');
            return;
        }

        setIsVerifying(true);
        setError('');

        try {
            const response = await axios.post(`${API}/auth/otp/verify`, {
                user_id: userId,
                otp_code: verificationCode
            });

            if (response.data.status === 'success') {
                toast.success('2FA u aktivizua me sukses!');
                onSetupComplete(true);
            }
        } catch (error) {
            setError('Kodi i verifikimit i pasaktë. Provoni përsëri.');
            console.error('OTP verification error:', error);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setVerificationCode(value);
        setError('');
    };

    const copySecretToClipboard = () => {
        if (otpData && otpData.base32_secret) {
            navigator.clipboard.writeText(otpData.base32_secret);
            setCopied(true);
            toast.success('Sekreti u kopjua në clipboard');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (isLoading) {
        return (
            <Card className="max-w-md mx-auto">
                <CardContent className="flex items-center justify-center p-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                        <p className="mt-2 text-gray-600">Duke gjeneruar 2FA setup...</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!otpData) {
        return (
            <Card className="max-w-md mx-auto">
                <CardContent className="text-center p-6">
                    <p className="text-red-600 mb-4">{error || 'Dështoi në ngarkimin e 2FA setup'}</p>
                    <Button onClick={generateOTPSecret}>
                        Provo Përsëri
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
                <div className="flex items-center justify-center mb-2">
                    <Shield className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Konfiguro Two-Factor Authentication</CardTitle>
                <CardDescription>
                    Skanoni këtë kod QR me aplikacionin tuaj authenticator
                </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
                <div className="text-center">
                    <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-4">
                            Skanoni me Google Authenticator, Microsoft Authenticator, ose aplikacion të ngjashëm
                        </p>
                        
                        <div className="flex justify-center mb-4 p-4 bg-white border rounded-lg">
                            {otpData.qr_code ? (
                                <img 
                                    src={otpData.qr_code} 
                                    alt="2FA QR Code"
                                    className="max-w-full h-auto"
                                />
                            ) : (
                                <QRCodeCanvas 
                                    value={otpData.otpauth_url} 
                                    size={200}
                                    level="M"
                                    includeMargin={true}
                                />
                            )}
                        </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-xs text-gray-600 mb-2">
                            Nuk mund të skanoni? Vendosni këtë kod manualisht:
                        </p>
                        <div className="flex items-center justify-between bg-white p-2 rounded border">
                            <p className="font-mono text-sm break-all flex-1">
                                {otpData.base32_secret}
                            </p>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={copySecretToClipboard}
                                className="ml-2 p-1"
                            >
                                {copied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>
                </div>

                <div>
                    <Label htmlFor="verification-code" className="block text-sm font-medium mb-2">
                        Vendosni kodin e verifikimit nga aplikacioni juaj authenticator:
                    </Label>
                    <Input
                        type="text"
                        id="verification-code"
                        value={verificationCode}
                        onChange={handleCodeChange}
                        placeholder="000000"
                        className="text-center text-lg font-mono tracking-wider"
                        maxLength={6}
                    />
                    {error && (
                        <p className="text-red-600 text-sm mt-2">{error}</p>
                    )}
                </div>

                <Button
                    onClick={verifyOTPCode}
                    disabled={isVerifying || verificationCode.length !== 6}
                    className="w-full"
                >
                    {isVerifying ? 'Duke verifikuar...' : 'Verifiko & Aktivizo 2FA'}
                </Button>
            </CardContent>
        </Card>
    );
};

export default TwoFactorSetup;